import React, { useEffect, useState } from 'react';
import API from '../../services/api.js';
import { useSearchParams } from 'react-router-dom';
import LoadingPopup from '../misc/Loading.js';

const Tenants = () => {

  const [fetchedclienttable, setFetchedClientTable] = useState(null);
  const [fetchedUsers, setFetchedUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [deleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const tenantName = searchParams.get('tenant_name');
  
  // Hardcoded features for now - will be replaced with API data later
  const allFeatures = [
    { name: 'Image Model Testing', color: 'bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30 text-blue-400' },
    { name: 'LLM Testing', color: 'bg-gradient-to-br from-purple-600/20 to-purple-800/20 border-purple-500/30 text-purple-400' },
    { name: 'System Prompt Hardening', color: 'bg-gradient-to-br from-amber-600/20 to-amber-800/20 border-amber-500/30 text-amber-400' },
    { name: 'GuardRails', color: 'bg-gradient-to-br from-red-600/20 to-red-800/20 border-red-500/30 text-red-400' },
    { name: 'Static Code Analysis', color: 'bg-gradient-to-br from-emerald-600/20 to-emerald-800/20 border-emerald-500/30 text-emerald-400' }
  ];
  
  const fetchClientTable = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/tenants/`);
      setFetchedClientTable(response.data);
    } catch (err) {
      setError(err.message || "Failed to fetch client data");
      console.error("Error fetching JSON:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (selectedTenantName) => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      let requestBody = {};
      if (selectedTenantName) {
        requestBody.tenantName = selectedTenantName;
      }
      const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/get-users/`, requestBody);
      setFetchedUsers(response.data);
    } catch (err) {
      setUsersError(err.message || "Failed to fetch users data");
      console.error("Error fetching users:", err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handler for View button click
  const handleViewTenant = async (selectedTenantName) => {
    if (expandedTenant === selectedTenantName) {
      // Collapse tenant
      setExpandedTenant(null);
      setFetchedUsers(null);
      // Clear URL param
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tenant_name');
      setSearchParams(newSearchParams);
    } else {
      // Expand tenant and fetch users
      setExpandedTenant(selectedTenantName);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tenant_name', selectedTenantName);
      setSearchParams(newSearchParams);
      await fetchUsers(selectedTenantName);
    }
  };

// Delete tenant by tenant name
const handleDeleteTenant = async (tenantNameToDelete) => {
  if (!window.confirm(`Are you sure you want to delete tenant "${tenantNameToDelete}"? This action cannot be undone.`)) {
    return;
  }
  try {
    setLoading(true); // Start loading animation
    setDeleteError(null);

    const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/delete-tenant/`, {
      tenantName: tenantNameToDelete
    });

    // Fetch fresh data
    await fetchClientTable();

    // Close expanded tenant if deleted
    if (expandedTenant === tenantNameToDelete) {
      setExpandedTenant(null);
      setFetchedUsers(null);
      setSearchParams(new URLSearchParams());
    }

    alert(response.data.message || "Tenant deleted successfully!");
  } catch (error) {
    setDeleteError(error.message || "Failed to delete tenant");
    console.error("Delete tenant error:", error);
  } finally {
    setLoading(false); // Stop loading animation
  }
};

// Delete user by username and tenant name
const handleDeleteUser = async (username, tenantName) => {
  if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
    return;
  }
  try {
    setUsersLoading(true); // Start users loading animation
    setDeleteError(null);

    const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/delete-user/`, {
      username,
      tenantName
    });

    // Fetch fresh user data and tenant data (to update user count)
    await Promise.all([
      fetchUsers(tenantName),
      fetchClientTable()
    ]);

    alert(response.data.message || "User deleted successfully!");
  } catch (error) {
    setDeleteError(error.message || "Failed to delete user");
    console.error("Delete user error:", error);
  } finally {
    setUsersLoading(false); // Stop users loading animation
  }
};


  useEffect(() => {
    const controller = new AbortController();
    
    fetchClientTable();
    
    if (tenantName) {
      setExpandedTenant(tenantName);
      fetchUsers(tenantName);
    }

    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tenantDetails = fetchedclienttable?.tenant_details || [];
  const users = fetchedUsers?.username_list || [];
  const roles = fetchedUsers?.roleList || [];

  return (
    <div className="m-12">
      {(loading || usersLoading) && <LoadingPopup isVisible={true} />}

      {deleteError && (
        <div className="text-center mt-4 text-red-400 bg-red-900/20 rounded-lg p-3 font-medium">
          {deleteError}
        </div>
      )}

      <div className="border border-[#232325] rounded-xl overflow-hidden">
        {error ? (
          <div className="bg-[#0D0D0E] text-red-400 p-6 text-center rounded-xl border border-red-500/30 bg-red-500/10">
            {error}
          </div>
        ) : (
          <div>
            <table className="min-w-full divide-y divide-[#232325]">
              <thead className="bg-[#121212] text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-md font-medium">S.No</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Tenant Name</th>
                  <th className="px-6 py-3 text-center text-md font-medium">User Count</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Application Count</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Features</th>
                  <th className="px-6 py-3 text-center text-md font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#232325] bg-[#0D0D0E] text-white">
                {tenantDetails.length > 0 ? (
                  tenantDetails.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="hover:bg-[#1a1a1c] transition-colors">
                        <td className="px-6 py-4 text-md">{idx + 1}</td>
                        <td className="px-6 py-4 text-center text-md">{item.tenant_name}</td>
                        <td className="px-6 py-4 text-center text-md">{item.user_count}</td>
                        <td className="px-6 py-4 text-center text-md">{item.asset_count}</td>
                        <td className="px-6 py-4 text-center text-md">
                          <div className="flex flex-wrap gap-2 justify-center">
                            {item.features && item.features.length > 0 ? (
                              item.features.map((featureName, featureIdx) => {
                                const feature = allFeatures.find(f => f.name === featureName);
                                const featureColor = feature
                                  ? feature.color
                                  : 'bg-gray-600/20 border border-gray-500/30 text-gray-400';
                                return (
                                  <span
                                    key={featureIdx}
                                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${featureColor}`}
                                  >
                                    {featureName}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full border border-gray-500/30 text-gray-400">
                                No Features
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-md text-right flex justify-end items-center gap-2">
                          <button
                            onClick={() => handleViewTenant(item.tenant_name)}
                            className={`py-1 px-3 rounded-xl transition-all duration-200 ${
                              expandedTenant === item.tenant_name
                                ? 'bg-blue-600 text-white hover:bg-blue-700 transform rotate-180'
                                : 'bg-[#232325] text-white hover:bg-[#1c1c1e]'
                            }`}
                          >
                            {expandedTenant === item.tenant_name ? '▲' : 'View'}
                          </button>
                          <button
                            onClick={() => handleDeleteTenant(item.tenant_name)}
                            className="py-1 px-3 rounded-xl bg-red-500/70 text-white hover:bg-red-600/80 transition-all duration-200 whitespace-nowrap"
                            disabled={deleteLoading}
                          >
                            Delete Tenant
                          </button>
                        </td>
                      </tr>

                      {/* Expanded users table row */}
                      {expandedTenant === item.tenant_name && (
                        <tr>
                          <td colSpan="6" className="px-0 py-0">
                            <div className="bg-[#1a1a1c] border-t border-[#232325] overflow-hidden transition-all duration-300 ease-in-out">
                              <div className="p-6">
                                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                                  Users for {item.tenant_name}
                                </h4>

                                {usersError ? (
                                  <div className="text-center py-8">
                                    <div className="text-red-400 bg-red-900/20 rounded-lg p-4">
                                      <div className="font-medium">Error loading users</div>
                                      <div className="text-sm mt-1">{usersError}</div>
                                    </div>
                                  </div>
                                ) : users.length > 0 ? (
                                  <div className="rounded-lg border border-[#232325] overflow-hidden">
                                    <table className="min-w-full">
                                      <thead className="bg-[#232325]">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">S.No</th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Username</th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Role</th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-[#232325] bg-[#1a1a1c]">
                                        {users.map((user, userIdx) => (
                                          <tr key={userIdx} className="hover:bg-[#232325] transition-colors">
                                            <td className="px-4 py-3 text-sm text-white">{userIdx + 1}</td>
                                            <td className="px-4 py-3 text-sm text-white font-medium">{user || 'N/A'}</td>
                                            <td className="px-4 py-3 text-sm">
                                              <span
                                                className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${
                                                  roles[userIdx]?.trim() === 'client_admin'
                                                    ? 'bg-blue-600/20 border border-blue-500/40 text-blue-300'
                                                    : roles[userIdx]?.trim() === 'viewer'
                                                    ? 'bg-emerald-600/20 border border-emerald-500/40 text-emerald-300'
                                                    : 'bg-[#232325] border border-[#232325] text-gray-500'
                                                }`}
                                              >
                                                {roles[userIdx]?.trim() === 'client_admin'
                                                  ? 'Client Administrator'
                                                  : roles[userIdx]?.trim() === 'viewer'
                                                  ? 'Client Viewer'
                                                  : roles[userIdx]?.trim() || 'User'}
                                              </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                              <button
                                                onClick={() => handleDeleteUser(user, item.tenant_name)}
                                                className="py-1 px-3 rounded-xl bg-red-500/70 text-white hover:bg-red-600/80 transition-all duration-200"
                                                disabled={deleteLoading}
                                              >
                                                Delete User
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="text-center py-8">
                                    <div className="text-gray-400">
                                      <div className="text-4xl mb-2">👥</div>
                                      <div className="font-medium">No users found</div>
                                      <div className="text-sm">This tenant has no associated users.</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-sm text-center text-white">
                      No tenants found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tenants;
